// src/pages/ReceivedCollections.tsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
   Box,
   Typography,
   Card,
   CardMedia,
   CardContent,
   CardActions,
   Button,
   Grid,
   Chip,
   CircularProgress,
   Alert,
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   Snackbar,
   Paper,
   Divider,
   Stack,
   Skeleton,
} from '@mui/material';
import {
   Delete as DeleteIcon,
   Visibility as VisibilityIcon,
   PhotoLibrary as PhotoLibraryIcon,
   Person as PersonIcon,
   AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useUser } from '../contexts/UserContext';
import SharedCollectionService from '../services/sharedCollectionService';
import { SharedCollection } from '../types/SharedCollection';
import { useNavigate } from 'react-router-dom';

// Memoized components for better performance
const CollectionCard = React.memo(
   ({
      collection,
      onView,
      onDelete,
      isProcessing,
   }: {
      collection: SharedCollection;
      onView: (shareToken: string | undefined) => void;
      onDelete: (id: string, title: string) => void;
      isProcessing: boolean;
   }) => {
      const getStatusChip = useCallback((status: string) => {
         switch (status) {
            case 'viewed':
               return <Chip label='Viewed' color='info' size='small' icon={<VisibilityIcon />} />;
            default:
               return <Chip label='New' color='primary' size='small' icon={<AccessTimeIcon />} />;
         }
      }, []);

      const getEntityTypeIcon = useCallback((entityType: string) => {
         switch (entityType) {
            case 'trip':
               return '✈️';
            case 'event':
               return '📅';
            case 'album':
               return '📸';
            default:
               return '📁';
         }
      }, []);

      const formattedDate = useMemo(() => {
         if (collection.createdAt && !isNaN(new Date(collection.createdAt).getTime())) {
            return format(new Date(collection.createdAt), 'MMM dd, yyyy');
         }
         return 'Unknown date';
      }, [collection.createdAt]);

      const photoPreview = useMemo(() => {
         if (!collection.photos || collection.photos.length === 0) {
            return (
               <Box
                  sx={{
                     height: 200,
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     backgroundColor: 'grey.100',
                  }}
               >
                  <PhotoLibraryIcon sx={{ fontSize: 48, color: 'grey.400' }} />
               </Box>
            );
         }

         return (
            <Box sx={{ position: 'relative', height: 200 }}>
               <Box sx={{ display: 'flex', height: '100%' }}>
                  <CardMedia
                     component='img'
                     sx={{
                        width: collection.photos.length === 1 ? '100%' : '50%',
                        objectFit: 'cover',
                     }}
                     image={collection.photos[0].thumbnailUrl || collection.photos[0].url}
                     alt='Collection preview'
                     loading='lazy'
                  />
                  {collection.photos.length > 1 && (
                     <Box sx={{ width: '50%', display: 'flex', flexDirection: 'column' }}>
                        {collection.photos.slice(1, 3).map((photo, index) => (
                           <CardMedia
                              key={photo.id}
                              component='img'
                              sx={{
                                 height: collection.photos.length > 2 ? '50%' : '100%',
                                 objectFit: 'cover',
                              }}
                              image={photo.thumbnailUrl || photo.url}
                              alt='Collection preview'
                              loading='lazy'
                           />
                        ))}
                     </Box>
                  )}
                  {collection.photos.length > 3 && (
                     <Box
                        sx={{
                           position: 'absolute',
                           bottom: 8,
                           right: 8,
                           backgroundColor: 'rgba(0,0,0,0.7)',
                           color: 'white',
                           borderRadius: 1,
                           px: 1,
                           py: 0.5,
                           fontSize: '0.75rem',
                        }}
                     >
                        +{collection.photos.length - 3} more
                     </Box>
                  )}
               </Box>
            </Box>
         );
      }, [collection.photos]);

      return (
         <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {photoPreview}

            <CardContent sx={{ flexGrow: 1 }}>
               <Stack direction='row' spacing={1} alignItems='center' mb={1}>
                  {getStatusChip((collection as any).userStatus)}
                  <Typography variant='body2' color='textSecondary'>
                     {getEntityTypeIcon(collection.entityType)} {collection.entityType}
                  </Typography>
               </Stack>

               <Typography variant='h6' component='h2' gutterBottom noWrap>
                  {collection.title ||
                     collection.subject ||
                     `Photos from ${collection.entityTitle}`}
               </Typography>

               <Typography variant='body2' color='textSecondary' gutterBottom>
                  <PersonIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                  Shared by {collection.senderName || 'Someone'}
               </Typography>

               <Typography variant='body2' color='textSecondary' gutterBottom>
                  <AccessTimeIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                  {formattedDate}
               </Typography>

               <Typography variant='body2' color='textSecondary'>
                  <PhotoLibraryIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                  {collection.photos?.length || 0} photos
               </Typography>

               {collection.message && (
                  <Box mt={1} p={1} bgcolor='grey.50' borderRadius={1}>
                     <Typography variant='body2' style={{ fontStyle: 'italic' }}>
                        "{collection.message}"
                     </Typography>
                  </Box>
               )}
            </CardContent>

            <Divider />

            <CardActions
               sx={{ p: 2, justifyContent: collection.shareToken ? 'space-between' : 'flex-end' }}
            >
               {collection.shareToken && (
                  <Button
                     size='small'
                     startIcon={<VisibilityIcon />}
                     onClick={() => onView(collection.shareToken)}
                  >
                     View
                  </Button>
               )}

               <Button
                  size='small'
                  variant='outlined'
                  color='error'
                  startIcon={<DeleteIcon />}
                  disabled={isProcessing}
                  onClick={() =>
                     onDelete(
                        collection.id,
                        collection.title || `Photos from ${collection.entityTitle}`
                     )
                  }
               >
                  Delete
               </Button>
            </CardActions>
         </Card>
      );
   }
);

// Skeleton loading component
const CollectionSkeleton = React.memo(() => (
   <Card sx={{ height: '100%' }}>
      <Skeleton variant='rectangular' height={200} />
      <CardContent>
         <Skeleton variant='text' width='60%' height={24} />
         <Skeleton variant='text' width='80%' height={20} sx={{ mt: 1 }} />
         <Skeleton variant='text' width='40%' height={20} sx={{ mt: 1 }} />
         <Skeleton variant='text' width='50%' height={20} sx={{ mt: 1 }} />
      </CardContent>
   </Card>
));

const ReceivedCollections: React.FC = () => {
   const { currentUser } = useUser();
   const navigate = useNavigate();
   const [collections, setCollections] = useState<SharedCollection[]>([]);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState<string | null>(null);
   const [processingCollectionId, setProcessingCollectionId] = useState<string | null>(null);
   const [confirmDialog, setConfirmDialog] = useState<{
      open: boolean;
      collectionId: string;
      collectionTitle: string;
   }>({
      open: false,
      collectionId: '',
      collectionTitle: '',
   });
   const [snackbar, setSnackbar] = useState<{
      open: boolean;
      message: string;
      severity: 'success' | 'error' | 'info';
   }>({
      open: false,
      message: '',
      severity: 'info',
   });

   // Data loading function
   const loadReceivedCollections = useCallback(async () => {
      if (!currentUser) return;

      try {
         setLoading(true);
         setError(null);

         console.log('🔄 Fetching received collections');
         const receivedCollections = await SharedCollectionService.getReceivedCollections();

         setCollections(receivedCollections);
      } catch (err) {
         console.error('Error loading received collections:', err);
         setError('Failed to load shared collections');
      } finally {
         setLoading(false);
      }
   }, [currentUser]);

   useEffect(() => {
      loadReceivedCollections();
   }, [loadReceivedCollections]);

   const handleDeleteCollection = useCallback(
      async (collectionId: string) => {
         try {
            setProcessingCollectionId(collectionId);
            await SharedCollectionService.deleteCollectionForUser(collectionId);

            setSnackbar({
               open: true,
               message: 'Collection removed from your view',
               severity: 'success',
            });

            // Refresh the collections list
            await loadReceivedCollections();
         } catch (err) {
            console.error('Error deleting collection:', err);
            setSnackbar({
               open: true,
               message: 'Failed to delete collection. Please try again.',
               severity: 'error',
            });
         } finally {
            setProcessingCollectionId(null);
            setConfirmDialog({ open: false, collectionId: '', collectionTitle: '' });
         }
      },
      [currentUser, loadReceivedCollections]
   );

   const handleViewCollection = useCallback((shareToken: string | undefined) => {
      if (shareToken) {
         window.open(`/shared/collection/${shareToken}`, '_blank');
      }
   }, []);

   const handleDeleteClick = useCallback((id: string, title: string) => {
      setConfirmDialog({
         open: true,
         collectionId: id,
         collectionTitle: title,
      });
   }, []);

   // Memoized empty state
   const emptyState = useMemo(
      () => (
         <Paper sx={{ p: 4, textAlign: 'center', mt: 3 }}>
            <PhotoLibraryIcon sx={{ fontSize: 60, color: 'grey.400', mb: 2 }} />
            <Typography variant='h6' color='textSecondary' gutterBottom>
               No shared collections yet
            </Typography>
            <Typography variant='body2' color='textSecondary'>
               When someone shares photo collections with you, they'll appear here.
            </Typography>
         </Paper>
      ),
      []
   );

   // Memoized loading skeletons
   const loadingSkeletons = useMemo(
      () => (
         <Grid container spacing={3}>
            {Array.from({ length: 6 }).map((_, index) => (
               <Grid item xs={12} sm={6} md={4} key={index}>
                  <CollectionSkeleton />
               </Grid>
            ))}
         </Grid>
      ),
      []
   );

   if (loading) {
      return (
         <Box px={3} pb={3}>
            {loadingSkeletons}
         </Box>
      );
   }

   if (error) {
      return (
         <Box p={3}>
            <Alert
               severity='error'
               action={
                  <Button color='inherit' size='small' onClick={loadReceivedCollections}>
                     Retry
                  </Button>
               }
            >
               {error}
            </Alert>
         </Box>
      );
   }

   return (
      <Box px={3} pb={3}>
         {collections.length === 0 ? (
            emptyState
         ) : (
            <Grid container spacing={3}>
               {collections.map((collection) => (
                  <Grid item xs={12} sm={6} md={4} key={collection.id}>
                     <CollectionCard
                        collection={collection}
                        onView={handleViewCollection}
                        onDelete={handleDeleteClick}
                        isProcessing={processingCollectionId === collection.id}
                     />
                  </Grid>
               ))}
            </Grid>
         )}

         {/* Confirmation Dialog */}
         <Dialog
            open={confirmDialog.open}
            onClose={() => setConfirmDialog({ open: false, collectionId: '', collectionTitle: '' })}
            maxWidth='sm'
            fullWidth
         >
            <DialogTitle>Delete Collection</DialogTitle>
            <DialogContent>
               <Typography>
                  Are you sure you want to delete "{confirmDialog.collectionTitle}" from your view?
               </Typography>
               <Alert severity='warning' sx={{ mt: 2 }}>
                  <Typography variant='body2'>
                     This will remove the collection from your view only. The owner and other
                     recipients will still have access to it.
                  </Typography>
               </Alert>
            </DialogContent>
            <DialogActions>
               <Button
                  onClick={() =>
                     setConfirmDialog({ open: false, collectionId: '', collectionTitle: '' })
                  }
                  disabled={processingCollectionId === confirmDialog.collectionId}
               >
                  Cancel
               </Button>
               <Button
                  onClick={() => handleDeleteCollection(confirmDialog.collectionId)}
                  color='error'
                  variant='contained'
                  disabled={processingCollectionId === confirmDialog.collectionId}
                  startIcon={
                     processingCollectionId === confirmDialog.collectionId ? (
                        <CircularProgress size={16} />
                     ) : undefined
                  }
               >
                  Delete
               </Button>
            </DialogActions>
         </Dialog>

         {/* Snackbar */}
         <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
         >
            <Alert
               onClose={() => setSnackbar({ ...snackbar, open: false })}
               severity={snackbar.severity}
               sx={{ width: '100%' }}
            >
               {snackbar.message}
            </Alert>
         </Snackbar>
      </Box>
   );
};

export default ReceivedCollections;
